import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './AdminLayout.css';

const AdminLayout = () => {
  const { isAuthenticated, isAdmin, currentUser, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="admin-access-denied">
        <div className="access-denied-content">
          <h2>🚫 Access Denied</h2>
          <p>You don't have administrator privileges to access this area.</p>
          <Link to="/" className="back-to-app-button">
            ← Back to Chat
          </Link>
        </div>
      </div>
    );
  }

  const navigationItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/admin/users', label: 'User Management', icon: '👥' },

    { path: '/admin/analytics', label: 'Analytics', icon: '📈' },
    { path: '/admin/system-health', label: 'System Health', icon: '🏥' },
    { path: '/admin/dynamodb-stats', label: 'DynamoDB Stats', icon: '🗄️' },
  ];

  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    
    // Always start with Admin
    breadcrumbs.push({ label: 'Admin', path: '/admin/dashboard' });
    
    // Add specific page breadcrumbs
    if (pathSegments.length > 1) {
      const currentPage = pathSegments[1];
      switch (currentPage) {
        case 'dashboard':
          // Dashboard is already the root, no additional breadcrumb needed
          break;
        case 'users':
          breadcrumbs.push({ label: 'User Management', path: '/admin/users' });
          if (pathSegments[2]) {
            const userId = pathSegments[2];
            breadcrumbs.push({ label: `User: ${userId}`, path: `/admin/users/${userId}` });
            
            // Handle mobile conversation routes
            if (pathSegments[3] === 'conversations') {
              breadcrumbs.push({ label: 'Conversations', path: location.pathname });
            } else if (pathSegments[3] === 'conversation' && pathSegments[4]) {
              breadcrumbs.push({ label: 'Conversations', path: `/admin/users/${userId}/conversations` });
              breadcrumbs.push({ label: 'Conversation', path: location.pathname });
            } else if (pathSegments[3] === 'chats' && pathSegments[4]) {
              breadcrumbs.push({ label: 'Chat History', path: location.pathname });
            }
          }
          break;
        case 'analytics':
          breadcrumbs.push({ label: 'Analytics', path: '/admin/analytics' });
          break;
        case 'system-health':
          breadcrumbs.push({ label: 'System Health', path: '/admin/system-health' });
          break;
        case 'dynamodb-stats':
          breadcrumbs.push({ label: 'DynamoDB Stats', path: '/admin/dynamodb-stats' });
          break;

        default:
          breadcrumbs.push({ label: currentPage, path: location.pathname });
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="admin-layout">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          {isMobile && (
            <button 
              className="mobile-menu-toggle" 
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              <span className={`hamburger ${sidebarOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          )}
          <Link to="/" className="back-to-chat">
            ← Back to Chat
          </Link>
          <h1>🛠️ Admin Panel</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-info">
            👤 {currentUser?.email} (Admin)
          </span>
          <button onClick={logout} className="admin-logout-button">
            🚪 Logout
          </button>
        </div>
      </header>

      <div className="admin-content-wrapper">
        {/* Mobile Sidebar Overlay */}
        {isMobile && sidebarOpen && (
          <div 
            className="sidebar-overlay" 
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Admin Sidebar Navigation */}
        <nav className={`admin-sidebar ${isMobile ? (sidebarOpen ? 'mobile-open' : 'mobile-closed') : ''}`}>
          <ul className="admin-nav-list">
            {navigationItems.map((item) => (
              <li key={item.path} className="admin-nav-item">
                <Link
                  to={item.path}
                  className={`admin-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="admin-nav-icon">{item.icon}</span>
                  <span className="admin-nav-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Main Admin Content */}
        <main className="admin-main">
          {/* Breadcrumb Navigation */}
          <div className="admin-breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <span className="breadcrumb-separator">›</span>}
                {index === breadcrumbs.length - 1 ? (
                  <span className="breadcrumb-current">{crumb.label}</span>
                ) : (
                  <Link to={crumb.path} className="breadcrumb-link">
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Admin Page Content */}
          <div className="admin-page-content">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;